'use client';

import { Autocomplete } from '@base-ui/react/autocomplete';
import { type ComponentProps, type SubmitEventHandler, useMemo } from 'react';

import { SearchControls, SearchPopup, SearchResults } from './components';
import { useSearchCoordinator, useSearchFlyout, useSearchOverlay } from './hooks';
import { mapSearchResults } from './lib';
import type { SearchResultItem } from './search.types';
import './search.scss';

type SearchInputKeyDownHandler = NonNullable<ComponentProps<typeof Autocomplete.Input>['onKeyDown']>;

const ignoreCommandFailure = () => undefined;

export function Search() {
    const {
        clearQuery,
        completeAssistedSearch,
        hasVisibleResults,
        onInputKeyDown,
        onQueryChange,
        query,
        refresh,
        selectSearchResult,
        sources,
        submitSearch,
    } = useSearchCoordinator();
    const { closeSearch, inputRef, onInputFocus, onOpenChange, open } = useSearchFlyout();
    const { controlsRef, formRef, isFullscreen, portalContainer, searchRootRef, setSearchRoot } =
        useSearchOverlay(open);
    const resultSources = useMemo(() => mapSearchResults(sources), [sources]);

    const handleCancel = () => {
        clearQuery();
        closeSearch();
    };

    const handleInputFocus = () => {
        if (onInputFocus()) refresh(query);
    };

    const handleResult = (result: SearchResultItem) => {
        const command = result.action === 'primary' ? selectSearchResult(result) : completeAssistedSearch(result);
        void command.catch(ignoreCommandFailure);
    };

    const handleInputKeyDown: SearchInputKeyDownHandler = event => {
        onInputKeyDown(event);
    };

    const handleSubmit: SubmitEventHandler<HTMLFormElement> = event => {
        event.preventDefault();
        if (!query.trim()) return;

        void submitSearch()
            .then(destination => {
                if (destination === 'search-results') closeSearch();
            })
            .catch(ignoreCommandFailure);
    };

    return (
        <div
            ref={setSearchRoot}
            className="header-search"
            data-fullscreen={open && isFullscreen ? '' : undefined}
            data-header-overlay-open={open ? '' : undefined}
        >
            <Autocomplete.Root
                grid
                items={resultSources}
                modal={isFullscreen}
                mode="none"
                onOpenChange={onOpenChange}
                onValueChange={onQueryChange}
                open={open}
                openOnInputClick
                value={query}
            >
                <SearchControls
                    controlsRef={controlsRef}
                    formRef={formRef}
                    inputRef={inputRef}
                    onCancel={open && isFullscreen ? handleCancel : undefined}
                    onInputFocus={handleInputFocus}
                    onInputKeyDown={handleInputKeyDown}
                    onSubmit={handleSubmit}
                    query={query}
                />

                {portalContainer && open && (
                    <SearchPopup
                        anchorRef={searchRootRef}
                        isFullscreen={isFullscreen}
                        portalContainer={portalContainer}
                    >
                        {hasVisibleResults && (
                            <SearchResults onResult={handleResult} query={query} sources={resultSources} />
                        )}
                    </SearchPopup>
                )}

                <Autocomplete.Status className="header-search__status" />
            </Autocomplete.Root>
        </div>
    );
}
